import type { ComponentProps, ReactNode } from "react"
import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack"
import { AuthUIProviderTanstack } from "@daveyplate/better-auth-ui/tanstack"
import { i18n } from "@lingui/core"
import { t } from "@lingui/core/macro"
import { I18nProvider } from "@lingui/react"
import { Link as RouterLink, useRouter } from "@tanstack/react-router"
import { Toaster } from "sonner"
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary"
import { ThemeProvider } from "@/components/theme-provider"
import { useTypedFlag } from "@/features/feature-flags/registry"
import { authClient } from "@/lib/auth-client"
import { env } from "@/lib/env"

export function Providers({ children }: { children: ReactNode }) {
  const router = useRouter()

  // Enable email/password auth based on environment variable
  const showEmailPassword = env.VITE_AUTH_ENABLE_EMAIL

  const DefaultI18n = ({ children }: { children: ReactNode }) => <span>{children}</span>

  DefaultI18n.displayName = "DefaultI18n"

  return (
    <AuthQueryProvider>
      <AppThemeProvider>
        <SentryErrorBoundary>
          <I18nProvider i18n={i18n} defaultComponent={DefaultI18n}>
            <AuthUIProviderTanstack
              authClient={authClient}
              navigate={(href) => void router.navigate({ href })}
              replace={(href) => void router.navigate({ href, replace: true })}
              Link={(
                props: Omit<ComponentProps<typeof RouterLink>, "to"> & {
                  href: string
                },
              ) => {
                const { href, children, ...linkProps } = props
                return (
                  <RouterLink to={href} {...linkProps}>
                    {children}
                  </RouterLink>
                )
              }}
              credentials={showEmailPassword}
              social={{
                providers: ["microsoft"],
              }}
              persistClient={true}
              signUp={false}
              localization={{
                SIGN_IN: t`Sign In`,
                SIGN_OUT: t`Sign Out`,
                SETTINGS: t`Settings`,
                DISABLED_CREDENTIALS_DESCRIPTION: "",
                SIGN_IN_WITH: t`Sign In With`,
              }}
            >
              {children}
              <Toaster />
            </AuthUIProviderTanstack>
          </I18nProvider>
        </SentryErrorBoundary>
      </AppThemeProvider>
    </AuthQueryProvider>
  )
}

function AppThemeProvider({ children }: { children: ReactNode }) {
  const { value: darkModeEnabled } = useTypedFlag("dark-mode")

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={darkModeEnabled}
      forcedTheme={darkModeEnabled ? undefined : "light"}
      disableTransitionOnChange
    >
      {children}
    </ThemeProvider>
  )
}
